import { Check, Plus, Times } from "icons/font-awesome/light"
import { useDomainContactApi } from "modules/api-client"
import { DomainContact } from "modules/api-client/src/domain-contact/types"
import { useFetchedApiResource } from "modules/helpers"
import { useModals } from "modules/modals/src/use-modals"
import { useNotifications } from "modules/notifications/src/use-notifications"
import { useTranslations } from "modules/translations"
import { useRouter } from "next/router"
import React, { useEffect } from "react"
import Button from "ui-kit/elements/button"
import LinkButton from "ui-kit/elements/link-button"
import Spinner from "ui-kit/elements/spinner"
import PageHeading from "ui-kit/headings/page-heading"
import Panel from "ui-kit/layout/panel"
import InteractiveTable, { PossibleInteractiveColumns } from "ui-kit/lists/interactive-table"

export default function DomainContactIndex (): JSX.Element {
    const { translate } = useTranslations()
    const { domainContactRequest } = useDomainContactApi()
    const {
        resource: DomainContactResponse,
        isLoading: fetchingDomainContact,
        fetchResource: fetchDomainContact,
    } = useFetchedApiResource( () => domainContactRequest() )
    useEffect( () => {
        if( !DomainContactResponse && ! fetchingDomainContact ) {
            fetchDomainContact()
        }
    }, [fetchDomainContact, DomainContactResponse, fetchingDomainContact] )
    return <>
        <PageHeading text={translate( "pages.domain-contact.index.page-header" )} id="domain-contact-heading">
            <LinkButton
                href="/domain-contact/new"
                icon={Plus}
            >
                {translate( "pages.domain-contact.index.button-add-domain-contact" )}
            </LinkButton>
        </PageHeading>
        <Panel title={translate( "pages.domain-contact.index.table-header" )}>
            {fetchingDomainContact &&
                <div className="flex flex-row items-center justify-center h-32">
                    <Spinner className="text-3xl text-brand-500" />
                </div>}
            {DomainContactResponse && !fetchingDomainContact && <DomainContactTable loading={fetchingDomainContact} domainContacts={DomainContactResponse} />}
        </Panel>
    </>
}
export function DomainContactTable ( props: { domainContacts: DomainContact[], loading: boolean } ): JSX.Element {
    const { translate } = useTranslations()
    const { setDefaultContactRequest } = useDomainContactApi()
    const { spawnModal } = useModals()
    const { pushNotification } = useNotifications()
    const { reload } = useRouter()
    interface TableRow extends DomainContact {
        id: number
        name: string
        amount_domains: number
        default: JSX.Element
        actions: JSX.Element
    }

    function setDefaultContactModal ( contact: DomainContact ): void
    {
        spawnModal( {
            type: "info",
            title: translate( "pages.domain-contact.index.modal.title" ),
            children: translate( "pages.domain-contact.index.modal.description" ),
            onConfirm: () => {
                setDefaultContactRequest( contact.id ).then( response => {
                    if ( !response.success ){
                        pushNotification( {
                            type: "error",
                            children: translate( "pages.domain-contact.index.modal.failure" ),
                        } )
                        return
                    }
                    pushNotification( {
                        type: "success",
                        children: translate( "general.saved" ),
                    } )

                    reload()
                } )
            },
            confirmText: translate( "pages.domain-contact.modal.make-default" ),
            dismissText: translate( "general.cancel" ),
        } )
    }

    const domainContactsToTable = ( contacts: DomainContact[] ): TableRow[] => contacts.map( contact => ( {
        ...contact,
        id: contact.id,
        name: contact.organization ?? `${contact.first_name} ${contact.last_name}`,
        amount_domains: contact.coupled_domains ? contact.coupled_domains.length : 0,
        default: contact.default_owner ? <Check aria-hidden="true" /> : <Times aria-hidden="true" />,
        actions: <div className="flex justify-content-between">
            <LinkButton href={`/domain-contact/${contact.id}/details`} size="sm">{translate( "pages.services.table_element_manage" )}</LinkButton>
            {!contact.default_owner && <Button onClick={() => setDefaultContactModal( contact )} className="ml-2" size="sm">{translate( "pages.domain-contact.index.table-default" )}</Button>}
        </div>,
    } ) )
    const tableColumns: Array<PossibleInteractiveColumns<TableRow>> = [
        {
            property: "name",
            header: translate( "pages.domain-contact.index.domain-contact-name" ),
        },
        {
            property: "amount_domains",
            header: translate( "pages.domain-contact.index.amount-domains-linked" ),
            searchable: false,
            sortable: false,
        },
        {
            property: "default",
            header: translate( "pages.domain-contact.index.default" ),
            searchable: false,
            sortable: false,
        },
        {
            property: "actions",
            header: translate( "pages.domain-contact.table_header.actions" ),
            searchable: false,
            sortable: false,
        },
    ]
    return <InteractiveTable labelledBy="domain-contact-heading"
        loading={props.loading}
        columns={tableColumns}
        data={domainContactsToTable( props.domainContacts )}
        keyProperty="id" />
}
